// ============================================================================
// scope-activity-sync.js - Phase 2 of WMS-first pipeline
// ----------------------------------------------------------------------------
// Keeps scope items in agreement with their source WMS activities.
//
//   • `mintActivityRefs(scope, wms)` - on fresh WMS generation, attaches an
//     `activityRef` to each scope item pointing at the activity whose id
//     matches the scope item's code (1:1 mapping for Phase 2). Items that
//     don't match any activity keep their existing activityRef (or null).
//
//   • `syncScopeFromActivities(scope, wms)` - mirrors the referenced
//     activity's costBreakdown onto an unresolved scope item's
//     `costBreakdown`. A strict Stage 3 pricing writeback is already the newer
//     authority and must not be replaced by the raw activity skeleton.
//     Respects `item.amountLocked` (the builder's pin overrides inheritance)
//     and `activity._needs_pricing` (skip; fall back to existing scope-item
//     pricing).
//
//   • `activityForScopeItem(item, wms)` - tiny helper used by UI chips that
//     show "Priced from activity N".
//
// Legacy projects (no pipelineVersion marker OR no wms.structured) get a
// no-op - their scope items keep their own costBreakdown authority.
// ============================================================================

import { hasReviewableManualCostAuthority } from './quote-pricing-readiness';
import { pricingRowHasPendingResolution } from '../services/pricing-resolution';
import {
    canonicaliseManualCostLedgerOwner,
    manualCostLedgerResolutionFields,
    partitionActivitiesByManualCostContributions,
} from './manual-cost-ledger-contributions';

function parseAmount(v) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (!v) return 0;
    const n = parseFloat(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

const uniqueStrings = (values = []) => Array.from(new Set(
    (Array.isArray(values) ? values : [])
        .map(value => String(value || '').trim())
        .filter(Boolean)
));

const activityIdFromRef = (ref) => {
    if (typeof ref === 'string' || typeof ref === 'number') return String(ref).trim();
    if (!ref || typeof ref !== 'object') return '';
    return String(ref.activity_id || ref.activityId || ref.id || '').trim();
};

const activityIdsForScopeItem = (item = {}) => {
    const ids = [];
    if (item?.activityRef != null) ids.push(activityIdFromRef(item.activityRef));
    if (Array.isArray(item?.activityRefs)) ids.push(...item.activityRefs.map(activityIdFromRef));
    if (Array.isArray(item?.activity_ids)) ids.push(...item.activity_ids);
    if (Array.isArray(item?.activityIds)) ids.push(...item.activityIds);
    if (Array.isArray(item?.activity_refs)) ids.push(...item.activity_refs.map(activityIdFromRef));
    return uniqueStrings(ids);
};

// Keep lineage repair independent of pricing inheritance. A v5.65.18 producer
// briefly emitted activityRef as a string, and the old pricing writeback spread
// that string into numeric object keys before an early return made it durable.
const canonicaliseScopeActivityLineage = (item, activityIds, wms) => {
    const primaryActivityId = activityIds[0];
    if (!primaryActivityId) return item;
    const currentRef = item?.activityRef;
    const currentRecord = currentRef && typeof currentRef === 'object' && !Array.isArray(currentRef)
        ? currentRef
        : {};
    const refKeys = Object.keys(currentRecord);
    const sameRef = currentRef === currentRecord
        && currentRecord.activityId === primaryActivityId
        && refKeys.every(key => ['activityId', 'sourceVersion', 'role_in_activity'].includes(key));
    const sameRefs = Array.isArray(item?.activityRefs)
        && item.activityRefs.length === activityIds.length
        && item.activityRefs.every((ref, index) => activityIdFromRef(ref) === activityIds[index]);
    const sameLegacyIds = Array.isArray(item?.activity_ids)
        && item.activity_ids.length === activityIds.length
        && item.activity_ids.every((id, index) => String(id) === activityIds[index]);
    if (sameRef && sameRefs && sameLegacyIds) return item;
    const sourceVersion = currentRecord.sourceVersion || wms?.version || item?.wmsVersion;
    const nextRef = sameRef ? currentRecord : {
        activityId: primaryActivityId,
        ...(sourceVersion ? { sourceVersion } : {}),
        role_in_activity: typeof currentRecord.role_in_activity === 'string'
            && currentRecord.role_in_activity.trim()
            ? currentRecord.role_in_activity
            : 'primary',
    };
    return {
        ...item,
        activityRef: nextRef,
        activityRefs: [...activityIds],
        activity_ids: [...activityIds],
    };
};

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const sumNumber = (objects, ...keys) => roundMoney(objects.reduce((sum, obj) => {
    for (const key of keys) {
        if (obj?.[key] != null) return sum + (Number(obj[key]) || 0);
    }
    return sum;
}, 0));

const averageNumber = (objects, ...keys) => {
    const values = objects
        .map(obj => {
            for (const key of keys) {
                if (obj?.[key] != null) return Number(obj[key]) || 0;
            }
            return null;
        })
        .filter(value => value != null);
    if (values.length === 0) return 0;
    return roundMoney(values.reduce((sum, value) => sum + value, 0) / values.length);
};

const combineActivityCosts = (activities = []) => {
    const stacks = activities.map(activity => activity?.costBreakdown).filter(Boolean);
    if (stacks.length === 0) return null;
    if (stacks.length === 1) {
        const cb = stacks[0];
        return {
            ...cb,
            materials: Array.isArray(cb.materials) ? [...cb.materials] : [],
            labour: Array.isArray(cb.labour) ? [...cb.labour] : [],
            equipment: Array.isArray(cb.equipment) ? [...cb.equipment] : [],
            subcontractors: Array.isArray(cb.subcontractors) ? [...cb.subcontractors] : [],
            otherCosts: Array.isArray(cb.otherCosts) ? [...cb.otherCosts] : (Array.isArray(cb.other) ? [...cb.other] : []),
        };
    }
    const directCostTotal = sumNumber(stacks, 'directCostTotal', 'direct_cost_total');
    const contingencyAmount = sumNumber(stacks, 'contingencyAmount', 'contingency_amount');
    const overheadAmount = sumNumber(stacks, 'overheadAmount', 'overhead_amount');
    const explicitTotalCost = sumNumber(stacks, 'totalCost', 'total_cost');
    return {
        materials: stacks.flatMap(cb => Array.isArray(cb.materials) ? cb.materials : []),
        labour: stacks.flatMap(cb => Array.isArray(cb.labour) ? cb.labour : []),
        equipment: stacks.flatMap(cb => Array.isArray(cb.equipment) ? cb.equipment : []),
        subcontractors: stacks.flatMap(cb => Array.isArray(cb.subcontractors) ? cb.subcontractors : []),
        otherCosts: stacks.flatMap(cb => Array.isArray(cb.otherCosts) ? cb.otherCosts : (Array.isArray(cb.other) ? cb.other : [])),
        directCostTotal,
        contingencyPct: averageNumber(stacks, 'contingencyPct', 'contingency_pct'),
        contingencyAmount,
        overheadPct: averageNumber(stacks, 'overheadPct', 'overhead_pct'),
        overheadAmount,
        totalCost: explicitTotalCost > 0 ? explicitTotalCost : roundMoney(directCostTotal + contingencyAmount + overheadAmount),
        marginPct: averageNumber(stacks, 'marginPct', 'margin_pct'),
        marginAmount: sumNumber(stacks, 'marginAmount', 'margin_amount'),
    };
};

// ─── Pending pricing findings must survive the mirror ───────────────────────
// The strict Stage 3 pricing boundary marks a line it could not price with pending-resolution markers
// (pricingStatus / _rateSource / unresolvedReason). Stage 4 coverage and Internal Pricing read those LINE
// markers: a line without them is a known cost. The activities of a grouped client row still hold the
// unpriced Stage 3 skeleton, which has no markers, and bindWmsToScopeProjection only copies a scope breakdown
// into an activity for an unambiguous 1:1 row. Mirroring that skeleton over reviewed lines turned unknown
// costs into plain zero lines (6.0.93 live review). The mirror is skipped only when it would drop a pending
// finding: every refresh that keeps the finding, or settles it with a priced line that matches the identity
// the finding asked for and supplies the driver it says is missing, still runs.
const PRICING_LINE_CATEGORIES = ['materials', 'labour', 'equipment', 'subcontractors', 'otherCosts'];

const pricingLineLabel = (line) => String(line?.description || line?.trade || line?.name || line?.item || '')
    .trim().toLowerCase().replace(/\s+/g, ' ');

const pricingLinesOf = (breakdown, category) => {
    if (category === 'otherCosts') {
        return [
            ...(Array.isArray(breakdown?.otherCosts) ? breakdown.otherCosts : []),
            ...(Array.isArray(breakdown?.other) ? breakdown.other : []),
        ];
    }
    return Array.isArray(breakdown?.[category]) ? breakdown[category] : [];
};

const hasPricingProvenance = (line) => Boolean(
    line?.pricingItemId || line?.pricing_item_id
    || line?._rateSource || line?._rate_source
    || line?.pricingStatus || line?.pricing_status,
);

const textOf = (value) => String(value ?? '').trim().toLowerCase();
const positiveNumber = (value) => parseAmount(value) > 0;
const quantityOf = (line) => line?.qty ?? line?.quantity;
const durationOf = (line) => line?.duration_days ?? line?.durationDays;

/**
 * The input each driver finding says is missing. A mirrored line answers a driver finding only when it
 * supplies that very driver: a positive total beside a quantity that is still zero does not.
 */
const DRIVER_SUPPLIED_BY = Object.freeze({
    invalid_material_quantity: (line) => positiveNumber(quantityOf(line)),
    invalid_equipment_quantity: (line) => positiveNumber(quantityOf(line)),
    invalid_labour_quantity: (line) => positiveNumber(quantityOf(line)),
    invalid_subcontractor_quantity: (line) => positiveNumber(quantityOf(line)),
    invalid_labour_hours: (line) => positiveNumber(line?.hours),
    invalid_labour_duration: (line) => positiveNumber(durationOf(line)),
    invalid_equipment_duration: (line) => positiveNumber(durationOf(line)),
    invalid_subcontractor_duration: (line) => positiveNumber(durationOf(line)),
    invalid_labour_workers: (line) => positiveNumber(line?.workers),
    invalid_subcontractor_workers: (line) => positiveNumber(line?.workers),
});

// Findings an authoritative price on the same item can settle. Every other reason (an ambiguous match,
// a supplier or overlap decision, an invalid id, a non-authoritative estimate) is a builder decision that
// an activity line cannot make, so only a line that still carries the finding answers it.
const SETTLED_BY_AN_AUTHORITATIVE_PRICE = new Set(['', 'no_catalogue_match']);

/**
 * Does `candidate` (a line of the mirrored breakdown) answer `pending` (a reviewed line of the scope item)?
 *   - It still carries a pending finding: the finding survives the mirror.
 *   - Or it is a priced result with provenance that matches the identity the finding asked for
 *     (requestedPricingItemId) and supplies the driver the finding says is missing.
 * A positive total with a provenance token is not enough on its own.
 */
function mirroredLineAnswersFinding(pending, candidate) {
    if (pricingRowHasPendingResolution(candidate)) return true;
    if (!positiveNumber(candidate?.total) || !hasPricingProvenance(candidate)) return false;
    const requested = String(pending?.requestedPricingItemId ?? pending?.requested_pricing_item_id ?? '').trim();
    const candidateId = String(candidate?.pricingItemId ?? candidate?.pricing_item_id ?? '').trim();
    if (requested && candidateId !== requested) return false;
    const reason = textOf(pending?.unresolvedReason ?? pending?.unresolved_reason);
    const suppliesDriver = DRIVER_SUPPLIED_BY[reason];
    if (suppliesDriver) return suppliesDriver(candidate);
    return SETTLED_BY_AN_AUTHORITATIVE_PRICE.has(reason);
}

function mirrorWouldDropPendingPricing(currentBreakdown, mirroredBreakdown) {
    for (const category of PRICING_LINE_CATEGORIES) {
        const unanswered = [];
        for (const line of pricingLinesOf(currentBreakdown, category)) {
            if (line && typeof line === 'object' && pricingRowHasPendingResolution(line)) unanswered.push(line);
        }
        if (unanswered.length === 0) continue;
        const candidates = pricingLinesOf(mirroredBreakdown, category)
            .filter(line => line && typeof line === 'object')
            .map(line => ({ line, label: pricingLineLabel(line), used: false }));
        for (const pending of unanswered) {
            const label = pricingLineLabel(pending);
            const answer = candidates.find(candidate => !candidate.used
                && candidate.label === label
                && mirroredLineAnswersFinding(pending, candidate.line));
            if (!answer) return true;
            answer.used = true;
        }
    }
    return false;
}

const combineActivityCostsPreservingManualContributions = (item, activities) => {
    const resolution = partitionActivitiesByManualCostContributions(item, activities);
    const { contributions, freshActivities } = resolution;
    if (!resolution.ok) {
        if (resolution.receipt?.reason === 'overlapping-manual-claims') {
            const selected = Array.isArray(resolution.selectedContributions)
                ? resolution.selectedContributions
                : [];
            const deterministic = combineActivityCosts([
                ...selected.map(entry => ({ costBreakdown: entry.costBreakdown })),
                ...freshActivities,
            ]);
            return {
                costBreakdown: deterministic
                    ? {
                        ...deterministic,
                        ...(freshActivities.length > 0 ? { costSource: 'mixed_manual_generated' } : {}),
                    }
                    : item?.costBreakdown || null,
                resolution,
            };
        }
        return { costBreakdown: item?.costBreakdown || null, resolution };
    }
    if (contributions.length === 0) {
        return { costBreakdown: combineActivityCosts(activities), resolution };
    }
    const combined = combineActivityCosts([
        ...contributions.map(entry => ({ costBreakdown: entry.costBreakdown })),
        ...freshActivities,
    ]);
    return {
        costBreakdown: combined
            ? { ...combined, costSource: 'mixed_manual_generated' }
            : item?.costBreakdown || null,
        resolution,
    };
};

/**
 * Attach `activityRef` to each scope item so downstream sync can look up
 * the source activity. Matches by `scope.code === activity.id` first, then
 * falls back to title match if the id scheme differs. Returns a new scope
 * array; input is not mutated.
 */
export function mintActivityRefs(scope, wms) {
    if (!Array.isArray(scope)) return scope;
    const activities = wms?.structured?.activities;
    if (!Array.isArray(activities) || activities.length === 0) return scope;
    const version = wms?.version || 'v1.0';

    // Build a lookup: activity.id → activity (authoritative)
    const byId = new Map();
    for (const a of activities) {
        if (a?.id) byId.set(String(a.id), a);
    }
    // Secondary lookup by normalised title (case-insensitive trim).
    const byTitle = new Map();
    for (const a of activities) {
        const key = (a?.title || '').trim().toLowerCase();
        if (key) byTitle.set(key, a);
    }

    return scope.map((item) => {
        const code = item?.code != null ? String(item.code) : null;
        const titleKey = (item?.title || '').trim().toLowerCase();
        const activity = (code && byId.get(code)) || byTitle.get(titleKey) || null;
        if (!activity) {
            // No match - leave any prior ref alone.
            return item;
        }
        return {
            ...item,
            activityRef: {
                activityId: activity.id,
                sourceVersion: version,
                role_in_activity: 'primary',
            },
        };
    });
}

/**
 * Mirror the source activity's authoritative `costBreakdown` onto each
 * referenced scope item's `costBreakdown`. Deliberately only touches the
 * breakdown - the existing Stage 4 derive (directCost + contingency \u00d7
 * (1 + margin) \u00d7 gstMult) recomputes `item.amount` from that breakdown.
 * Two-step on purpose so both legacy and activity-inherited items run
 * through the same amount-derive path, staying consistent.
 *
 * Respects locks:
 *   • `item.amountLocked` → do NOT mirror; the builder has pinned their own
 *     price AND implicitly their own cost breakdown. Leaving the scope
 *     breakdown untouched means the pinned amount stays coherent with it.
 *   • `activity._needs_pricing` → skip mirror (activity has no usable
 *     costBreakdown yet; scope item keeps its existing pricing).
 *
 * Returns a new scope array only when something changed - otherwise returns
 * the input reference unchanged, so React effects don't chain-fire.
 *
 * @param {Array} scope
 * @param {Object} wms  project.wms envelope
 * @returns {Array}
 */
export function syncScopeFromActivities(scope, wms) {
    if (!Array.isArray(scope)) return scope;
    const activities = wms?.structured?.activities;
    if (!Array.isArray(activities) || activities.length === 0) return scope;

    const byId = new Map();
    for (const a of activities) {
        if (a?.id) byId.set(String(a.id), a);
    }

    let changed = false;
    const next = scope.map((item) => {
        const canonicalItem = canonicaliseManualCostLedgerOwner(item);
        if (canonicalItem !== item) changed = true;
        const activityIds = activityIdsForScopeItem(canonicalItem);
        const referencedActivities = activityIds.map(id => byId.get(String(id))).filter(Boolean);
        const authorityResolution = partitionActivitiesByManualCostContributions(
            canonicalItem,
            referencedActivities,
        );
        if (!authorityResolution.ok) {
            const costProjection = combineActivityCostsPreservingManualContributions(
                canonicalItem,
                referencedActivities,
            );
            changed = true;
            return {
                ...canonicalItem,
                costBreakdown: costProjection.costBreakdown || canonicalItem.costBreakdown,
                ...manualCostLedgerResolutionFields(authorityResolution),
            };
        }
        if (activityIds.length === 0) return canonicalItem;
        if (referencedActivities.length === 0) return canonicalItem;
        const linedItem = canonicaliseScopeActivityLineage(canonicalItem, activityIds, wms);
        if (linedItem !== canonicalItem) changed = true;
        if (referencedActivities.some(activity => activity._needs_pricing || !activity.costBreakdown)) return linedItem;
        if (linedItem.amountLocked === true) return linedItem;
        // Stage 4's Price Working editor deliberately does not pin a manual
        // lump-sum amount. It stamps the recomputed ledger `manual_edit`
        // instead. Preserve that cost-specific receipt before mirroring the
        // older/generated activity skeleton over the builder's quantities and
        // rates. Unlocked generated ledgers continue through to the refresh.
        if (linedItem.costBreakdown && hasReviewableManualCostAuthority(linedItem)) return linedItem;

        // Strict WMS recompute resolves catalogue and market-price authority,
        // then stamps the scope row committed. The activity still carries the
        // earlier AI-authored skeleton. Mirroring that older skeleton here
        // silently discards resolved rates/evidence while leaving the stale
        // `pricingStatus: resolved` flag behind. Preserve the newer pricing
        // boundary; a later WMS regeneration replaces it explicitly through
        // the recompute/writeback path.
        if (
            linedItem.amountAuthority === 'committed'
            && linedItem.pricingStatus === 'resolved'
            && linedItem?.costBreakdown?._costSource === 'wms-recompute'
        ) return linedItem;

        const costProjection = combineActivityCostsPreservingManualContributions(linedItem, referencedActivities);
        if (!costProjection.resolution.ok) {
            changed = true;
            return {
                ...linedItem,
                costBreakdown: costProjection.costBreakdown || linedItem.costBreakdown,
                ...manualCostLedgerResolutionFields(costProjection.resolution),
            };
        }
        const activityCb = costProjection.costBreakdown;
        if (!activityCb) return item;
        const currentCb = linedItem.costBreakdown || {};
        const costSource = referencedActivities.length > 1 ? 'activity_group_inherited' : 'activity_inherited';
        const currentCanonicalIds = uniqueStrings([
            linedItem?.activityRef?.activityId,
            ...(Array.isArray(linedItem?.activityRefs) ? linedItem.activityRefs.map(activityIdFromRef) : []),
            ...(Array.isArray(linedItem?.activity_ids) ? linedItem.activity_ids : []),
        ]);
        const sameActivityRefs = currentCanonicalIds.length === activityIds.length
            && currentCanonicalIds.every((id, index) => id === activityIds[index]);

        // Cheap equality check on the load-bearing totals + a materials count
        // so we don't churn on micro-edits. If any of these differ, mirror.
        const sameTotals = Number(currentCb.directCostTotal) === Number(activityCb.directCostTotal)
            && Number(currentCb.contingencyAmount) === Number(activityCb.contingencyAmount)
            && Number(currentCb.overheadAmount) === Number(activityCb.overheadAmount)
            && Number(currentCb.totalCost) === Number(activityCb.totalCost)
            && (currentCb.costSource === costSource)
            && (currentCb.materials?.length === activityCb.materials?.length)
            && (currentCb.labour?.length === activityCb.labour?.length)
            && sameActivityRefs;
        if (sameTotals) return linedItem;
        // The activity side is older than the strict pricing boundary's review of this row. Keep the
        // reviewed breakdown (and the lineage repair above) rather than turning its unknowns into zeros.
        if (mirrorWouldDropPendingPricing(currentCb, activityCb)) return linedItem;

        changed = true;
        return {
            ...linedItem,
            costBreakdown: {
                ...activityCb,
                costSource,
                lastUpdated: new Date().toISOString(),
            },
            activityRef: linedItem.activityRef,
            activityRefs: activityIds,
            activity_ids: activityIds,
            ...manualCostLedgerResolutionFields(costProjection.resolution),
        };
    });

    return changed ? next : scope;
}

/**
 * UI helper - find the source activity for a scope item (or null).
 */
export function activityForScopeItem(item, wms) {
    const ref = item?.activityRef;
    if (!ref?.activityId) return null;
    const activities = wms?.structured?.activities;
    if (!Array.isArray(activities)) return null;
    return activities.find(a => String(a?.id) === String(ref.activityId)) || null;
}

/**
 * Whether a scope item's activityRef points at an activity that no longer
 * exists (scope kept, WMS regenerated with different activity ids) OR a
 * version that's drifted. UI can surface a "regenerate scope" hint when true.
 */
export function isScopeActivityStale(item, wms) {
    const ref = item?.activityRef;
    if (!ref?.activityId) return false;
    const activities = wms?.structured?.activities;
    if (!Array.isArray(activities)) return false;
    const found = activities.find(a => String(a?.id) === String(ref.activityId));
    if (!found) return true;
    const currentVer = wms?.version;
    return currentVer && ref.sourceVersion && currentVer !== ref.sourceVersion;
}

export const __testables = { parseAmount };
